import {
    AccessDto,
    CityDto,
    PhaseDto,
    ProjectFilterInput,
    ProjectRelatedEntitiesContainerDto,
    ProjectSearchType,
    RegionDto,
    SubRegionDto,
} from "../api/psc";

export function detectFilterChange(
    o1: ProjectFilterInput,
    o2: ProjectFilterInput
): boolean {
    const f1 = {
        ...o1,
        type: ProjectSearchType.ALL,
        targetAccessId: null,
    };

    const f2 = {
        ...o2,
        type: ProjectSearchType.ALL,
        targetAccessId: null,
    };

    return !object_equals(f1, f2);
}

function object_equals(x, y) {
    if (x === y) return true;
    // if both x and y are null or undefined and exactly the same

    if (!(x instanceof Object) || !(y instanceof Object)) return false;
    // if they are not strictly equal, they both need to be Objects

    if (x.constructor !== y.constructor) return false;
    // they must have the exact same prototype chain, the closest we can do is
    // test there constructor.

    for (var p in x) {
        if (!x.hasOwnProperty(p)) continue;
        // other properties were tested using x.constructor === y.constructor

        if (!y.hasOwnProperty(p)) {
            return false;
        }
        // allows to compare x[ p ] and y[ p ] when set to undefined

        if (x[p] === y[p]) continue;
        // if they have the same strict value or identity then they are equal

        if (typeof x[p] !== "object") return false;
        // Numbers, Strings, Functions, Booleans must be strictly equal

        if (!object_equals(x[p], y[p])) return false;
        // Objects and Arrays must be tested recursively
    }

    for (p in y)
        if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) {
            if (
                (y[p] === null || y[p] === undefined) &&
                (x[p] === null || x[p] === undefined)
            ) {
            } else {
                return false;
            }
        }
    // allows x[ p ] to be set to undefined

    return true;
}

interface ProjectFilterBaseData {
    cityIds?: number[];
    regionIds?: number[];
    subRegionIds?: number[];
    phaseIds?: number[];
}

export class ProjectFilterHelper {
    constructor(
        public relatedEntities: ProjectRelatedEntitiesContainerDto,
        public accessList: AccessDto[],
        public filter: ProjectFilterBaseData,
        public selectedAccess: AccessDto | null = null
    ) {}

    getCities(): CityDto[] {
        if (this.selectedAccess === null) {
            return [];
        }

        return this.relatedEntities.cities.filter((c) =>
            this.selectedAccess.cityIds.includes(c.id)
        );
    }

    getRegions(): RegionDto[] {
        if (this.selectedAccess === null) {
            return [];
        }

        return (
            this.getCities()
                .flatMap((c) => c.regions)
                // if any city is selected then filter regions based on selected city
                .filter(
                    (r) =>
                        this.filter.cityIds.length === 0 ||
                        this.filter.cityIds.includes(r.cityId)
                )
                // if selected acces has any limitation on regions
                // then filter regions based on selected access limitation
                .filter(
                    (r) =>
                        this.selectedAccess.regionIds.length === 0 ||
                        this.selectedAccess.regionIds.includes(r.id)
                )
        );
    }

    getSubRegions(): SubRegionDto[] {
        if (this.selectedAccess === null) {
            return [];
        }

        return (
            this.getRegions()
                .flatMap((c) => c.subregions)
                // if any region is selected then filter subregions based on selected region
                .filter(
                    (r) =>
                        this.filter.regionIds.length === 0 ||
                        this.filter.regionIds.includes(r.regionId)
                )
        );
    }

    getPhases(): PhaseDto[] {
        if (this.selectedAccess === null) {
            return [];
        }

        return this.relatedEntities.phaseList.filter(
            (p) =>
                this.selectedAccess.phaseIds.length === 0 ||
                this.selectedAccess.phaseIds.includes(p.id)
        );
    }
}

export class ProjectSelectedFilterHelper extends ProjectFilterHelper {
    constructor(
        public relatedEntities: ProjectRelatedEntitiesContainerDto,
        public accessList: AccessDto[],
        public filter: ProjectFilterBaseData,
        public selectedAccess: AccessDto | null = null
    ) {
        super(relatedEntities, accessList, filter, selectedAccess);
    }

    getSelectedRegions(): RegionDto[] {
        return this.getRegions().filter((r) =>
            this.filter.regionIds.includes(r.id)
        );
    }

    getSelectedSubRegions(): SubRegionDto[] {
        return this.getSubRegions().filter((sr) =>
            this.filter.subRegionIds.includes(sr.id)
        );
    }

    getSelectedPhases(): PhaseDto[] {
        return this.getPhases().filter((ph) =>
            this.filter.phaseIds.includes(ph.id)
        );
    }
}
